import { Button, Checkbox, Form, Input, InputNumber, Radio, Select, Space } from "antd";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
    DateRangePicker as ZhDateRangePicker,
    DetailHeader as ZhDetailHeader,
    Grid as ZhGrid,
    GridItem as ZhGridItem,
    Upload as ZhUpload,
    type UploadRequestOptions
} from "zhihao-ui-react";

import request from "@/api";
import SingleFileUpload from "@/views/components/Upload/singleFileUpload";
import WangEditor from "@/views/components/WangEditor";
import { loadActivitySpecials as defaultLoadActivitySpecials } from "./ActivityPage";
import {
    ActivityRelatedProductSelector,
    loadActivityRelatedProductCandidates,
    type ActivityRelatedProductSelectorResult
} from "./ActivityRelatedProductSelector";
import type { ActivityFile, ActivityRegistrationConfig, ActivitySpecial } from "./types";
import type { ActivityFormValues, ActivityRelatedProductItem, ActivityRow } from "./types";
import "./ActivityFormPage.scss";

/** 将 Vue 文件 ID 集合转换为表单输入框可编辑的逗号文本。 */
const formatFileIdInput = (value?: Array<number | string> | string) =>
    Array.isArray(value) ? value.join(",") : value || "";

/** 将表单中的逗号文本还原为 Vue 接口需要的数字 ID 集合。 */
const parseFileIdList = (value?: Array<number | string> | string) => {
    const values = Array.isArray(value) ? value : String(value || "").split(",");
    return values
        .map(item => String(item).trim())
        .filter(Boolean)
        .map(item => Number(item))
        .filter(item => Number.isFinite(item));
};

/** 活动主图上传允许的图片 MIME 类型和单文件大小上限。 */
const ACTIVITY_MAIN_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp", "image/avif"]);
const ACTIVITY_MAIN_IMAGE_MAX_SIZE = 10 * 1024 * 1024;

/** Vue 活动表单的独立白底区块，避免 Ant Card 再次添加 body 内边距。 */
function ActivityFormSection({
    title,
    children,
    className = ""
}: {
    title: string;
    children: ReactNode;
    className?: string;
}) {
    return (
        <section className={`section-card activity-form__section ${className}`.trim()}>
            <div className="section-title">{title}</div>
            {children}
        </section>
    );
}

interface ActivityImageUploadProps {
    label: string;
    image?: ActivityFile;
    uploadedId?: number | string;
    hint: string;
    accept: string;
    beforeUpload: (file: File) => boolean;
    httpRequest: (options: UploadRequestOptions) => void;
}

/** 单张活动图片上传，按 Vue UploadList 的 232×122 预览尺寸展示回显图片。 */
function ActivityImageUpload({
    label,
    image,
    uploadedId,
    hint,
    accept,
    beforeUpload,
    httpRequest
}: ActivityImageUploadProps) {
    const imageUrl = image?.path || image?.url;
    return (
        <div className="activity-form__upload">
            <p className="activity-form__upload-hint">{hint}</p>
            <ZhUpload
                action="#"
                accept={accept}
                aria-label={`${label}上传`}
                beforeUpload={file => beforeUpload(file as File)}
                httpRequest={httpRequest}
                limit={1}
                showFileList={false}
            >
                {imageUrl ? (
                    <span className="activity-form__upload-preview">
                        <img alt={`${label}预览`} src={imageUrl} />
                    </span>
                ) : (
                    <span className="activity-form__upload-trigger">
                        <i aria-hidden="true" className="iconfont icon-shangchuan" />
                        {uploadedId != null ? "替换图片" : "上传图片"}
                    </span>
                )}
            </ZhUpload>
            {uploadedId != null ? <span className="sr-only">{`${label}已上传：${uploadedId}`}</span> : null}
        </div>
    );
}

interface ActivityMediaUploadProps {
    label: string;
    files: ActivityFile[];
    hint: string;
    accept: string;
    limit: number;
    beforeUpload: (file: File) => boolean;
    httpRequest: (options: UploadRequestOptions) => void;
}

/** 报名区域图片上传列表，保留旧版的已上传缩略图和最大数量反馈。 */
function ActivityMediaUpload({
    label,
    files,
    hint,
    accept,
    limit,
    beforeUpload,
    httpRequest
}: ActivityMediaUploadProps) {
    return (
        <div className="activity-form__upload">
            <p className="activity-form__upload-hint">{hint}</p>
            <div className="activity-form__upload-list">
                {files.length < limit ? (
                    <ZhUpload
                        action="#"
                        accept={accept}
                        aria-label={`${label}上传`}
                        beforeUpload={file => beforeUpload(file as File)}
                        httpRequest={httpRequest}
                        limit={limit}
                        showFileList={false}
                    >
                        <span className="activity-form__upload-trigger">
                            <i aria-hidden="true" className="iconfont icon-shangchuan" />
                            上传图片
                        </span>
                    </ZhUpload>
                ) : null}
                {files.map((file, index) => {
                    const imageUrl = file.path || file.url;
                    return imageUrl ? (
                        <span className="activity-form__upload-preview" key={String(file.id ?? imageUrl)}>
                            <img alt={`${label}预览${index + 1}`} src={imageUrl} />
                        </span>
                    ) : null;
                })}
            </div>
        </div>
    );
}

/** 报名权益沿用 Vue 的 WangEditor，保留 Ant Form 的 value/onChange 接入方式。 */
function ActivityRichTextEditor({
    value = "",
    onChange,
    legacyLabel,
    height = "300px"
}: {
    value?: string;
    onChange?: (value: string) => void;
    legacyLabel?: string;
    height?: string;
}) {
    return (
        <div className="activity-form__rich-editor">
            <WangEditor height={height} placeholder="请输入权益介绍" value={value} onUpdateValue={onChange} />
            {legacyLabel ? (
                <textarea
                    aria-label={legacyLabel}
                    className="activity-form__legacy-control"
                    value={value}
                    onChange={event => onChange?.(event.target.value)}
                />
            ) : null}
        </div>
    );
}

/** 合同模版复用 Vue single-upload 的蓝色链接样式和文件 ID 回填契约。 */
function ActivityContractTemplateUpload({
    value,
    onChange
}: {
    value?: number | string;
    onChange?: (value?: number) => void;
}) {
    return (
        <div className="activity-form__contract-upload">
            <SingleFileUpload
                acceptType={["doc", "docx", "pdf"]}
                icon={true}
                modelValue={value == null || value === "" ? undefined : Number(value)}
                size={10}
                tip="上传文件"
                onUpdateModelValue={onChange}
            />
            <div className="activity-form__contract-hint">
                支持最多上传一个文件，支持上传DOC、DOCX、PDF格式，单个文件最大10MB
            </div>
        </div>
    );
}

const PARTICIPATION_OPTIONS = [
    { label: "冠名", value: 1 },
    { label: "协办", value: 2 },
    { label: "赞助", value: 3 },
    { label: "参展", value: 4 },
    { label: "普通参会", value: 5 }
];

const PARTICIPATION_FIELD_NAMES = ["企业抬头", "职务", "姓名", "手机号", "参会需求"];

/** 读取 Vue 关联商品在不同返回结构中的统一引用。 */
const getRelatedProductReference = (item: ActivityRelatedProductItem) =>
    item.id ?? item.productId ?? item.productCampaign?.code ?? item.productCampaign?.id;

/** 将 Vue ServiceSpecialVo 的嵌套用户结构归一化为表单下拉选项。 */
const normalizeActivitySpecial = (special: ActivitySpecial) => {
    const user = special.user;
    const id = special.id ?? user?.personId;
    const name = user?.name ?? special.name;
    const employeeId = user?.employeeId ?? special.employeeId;
    return {
        ...special,
        id,
        // Vue 旧版下拉框展示“姓名 | 工号”，没有工号时只展示姓名。
        name: name ? (employeeId ? `${name} | ${employeeId}` : name) : undefined
    };
};

/** 按 Vue 关联商品的 type:id 唯一键去重，兼容 id/productId 两种回显字段。 */
const dedupeRelatedProductItems = (items: ActivityRelatedProductItem[]) => {
    const seen = new Set<string>();
    const normalizedItems: ActivityRelatedProductItem[] = [];
    items.forEach(item => {
        const reference = getRelatedProductReference(item);
        const type = Number(item.type);
        if (![1, 2, 3, 4].includes(type) || reference == null) return;
        const key = `${type}:${String(reference).trim()}`;
        if (seen.has(key)) return;
        seen.add(key);
        // Vue 保存接口使用数字类型，即使编辑接口返回字符串也统一规范化。
        normalizedItems.push({ ...item, type });
    });
    return normalizedItems;
};

/** 将表单中的 type:id 文本转换为 Vue 活动关联商品结构。 */
const parseRelatedProductItems = (value?: ActivityRelatedProductItem[] | string): ActivityRelatedProductItem[] => {
    if (Array.isArray(value)) return dedupeRelatedProductItems(value);
    return dedupeRelatedProductItems(
        String(value || "")
            .split(",")
            .map(item => item.trim().split(":"))
            .filter(([type, id]) => type && id)
            .map(([type, id]) => {
                const parsedId = Number(id);
                return { type: Number(type), id: Number.isFinite(parsedId) ? parsedId : id };
            })
            .filter(item => Number.isFinite(item.type) && item.id != null && String(item.id).trim() !== "")
    );
};

export interface ActivityFormPageProps {
    loadActivity?: (id: number | string) => Promise<ActivityRow>;
    loadRelatedProducts?: (id: number | string) => Promise<ActivityRelatedProductItem[]>;
    loadRelatedProductCandidates?: (query: {
        page: number;
        size: number;
        keyword?: string;
        productTypeName?: string;
        supplierName?: string;
        type?: number;
        excludeProducts?: Array<{ productId: string; type: number }>;
    }) => Promise<ActivityRelatedProductSelectorResult>;
    loadSpecials?: (keyword: string) => Promise<ActivitySpecial[]>;
    saveActivity?: (id: number | string | undefined, values: ActivityFormValues) => Promise<void>;
    saveDraftActivity?: (values: ActivityFormValues) => Promise<void>;
}

/** 复用 Vue 活动详情接口，为编辑表单回填数据。 */
export async function loadActivity(id: number | string): Promise<ActivityRow> {
    const response = await request.service.get<ActivityRow>(`/admin/campaign/vo/${id}`);
    return response.data;
}

/** 复用 Vue 活动新增和编辑接口。 */
export async function saveActivity(id: number | string | undefined, values: ActivityFormValues): Promise<void> {
    if (id == null) {
        await request.service.post("/admin/campaign", values);
    } else {
        await request.service.put(`/admin/campaign/${id}`, values);
    }
}

/** 复用 Vue 活动草稿保存接口。 */
export async function saveDraftActivity(values: ActivityFormValues): Promise<void> {
    await request.service.post("/admin/campaign/draft", values);
}

/** 复用 Vue 活动编辑页关联商品接口。 */
export async function loadActivityRelatedProducts(id: number | string): Promise<ActivityRelatedProductItem[]> {
    const response = await request.service.get<ActivityRelatedProductItem[]>(`/admin/campaign/products/${id}`);
    return response.data;
}

/** 商品活动新增/编辑页，覆盖 Vue 表单的核心活动信息字段。 */
export function ActivityFormPage({
    loadActivity: load = loadActivity,
    loadRelatedProducts = loadActivityRelatedProducts,
    loadRelatedProductCandidates = loadActivityRelatedProductCandidates,
    loadSpecials = defaultLoadActivitySpecials,
    saveActivity: save = saveActivity,
    saveDraftActivity: saveDraft = saveDraftActivity
}: ActivityFormPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const query = new URLSearchParams(location.search);
    const detailId = query.get("id") || undefined;
    const campaignIdParam = query.get("campaignId") || detailId;
    const campaignId = campaignIdParam && /^\d+$/.test(campaignIdParam) ? Number(campaignIdParam) : campaignIdParam;
    // 对齐 Vue campaignTitle：有活动标识时展示编辑标题，否则展示新增标题。
    const campaignTitle = campaignId ? "编辑活动" : "新增活动";
    const [form] = Form.useForm<ActivityFormValues>();
    const dateRangePickerRef = useRef<HTMLDivElement>(null);
    const validTimeStart = Form.useWatch("validTimeStart", form);
    const validTimeFinish = Form.useWatch("validTimeFinish", form);
    const selectedContentConfigs = Form.useWatch("contentConfigs", form) || [];
    const selectedParticipationConfigs = Form.useWatch("meetingParticipationConfigs", form) || [];
    const meetingConfigs = Form.useWatch("campaignMeetingTypeConfigForms", form) || [];
    const demandConfigs = Form.useWatch("campaignDemandConfigForms", form) || [];
    const needAgreeProtocol = Form.useWatch("needAgreeProtocol", form);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [specials, setSpecials] = useState<ActivitySpecial[]>([]);
    const [loadedRelatedProductItems, setLoadedRelatedProductItems] = useState<ActivityRelatedProductItem[]>([]);
    const [activityState, setActivityState] = useState<number | undefined>();
    const [pcImgMainFile, setPcImgMainFile] = useState<ActivityFile>();
    const [mobileImgMainFile, setMobileImgMainFile] = useState<ActivityFile>();
    const [paymentQrCodeFile, setPaymentQrCodeFile] = useState<ActivityFile>();
    const [scheduleFile, setScheduleFile] = useState<ActivityFile>();
    const [meetingSupportFiles, setMeetingSupportFiles] = useState<ActivityFile[]>([]);
    const [conferenceHotelFiles, setConferenceHotelFiles] = useState<ActivityFile[]>([]);
    const [pastGloriesFiles, setPastGloriesFiles] = useState<ActivityFile[]>([]);
    const [bankOptions, setBankOptions] = useState<Array<{ label: string; value: number | string }>>([]);
    const pcImgMainId = Form.useWatch("pcImgMainId", form);
    const mobileImgMainId = Form.useWatch("mobileImgMainId", form);
    // Vue 仅允许新增或草稿（state=2）活动编辑专员和内容配置，已开启/已关闭活动均锁定。
    const formLocked = detailId !== undefined && activityState !== undefined && activityState !== 2;

    useEffect(() => {
        if (!detailId) {
            setActivityState(undefined);
            setLoadedRelatedProductItems([]);
            setPcImgMainFile(undefined);
            setMobileImgMainFile(undefined);
            setPaymentQrCodeFile(undefined);
            setScheduleFile(undefined);
            setMeetingSupportFiles([]);
            setConferenceHotelFiles([]);
            setPastGloriesFiles([]);
            form.setFieldsValue({ title: "", contentConfigs: [1] });
            return;
        }

        void load(detailId)
            .then(result => {
                // Vue 编辑接口的枚举可能以字符串返回，加载时统一为数字供表单锁定、复选框和协议分支使用。
                const normalizedCampaign: ActivityRow["campaign"] = {
                    ...result.campaign,
                    state: result.campaign.state == null ? undefined : Number(result.campaign.state),
                    contentConfigs: result.campaign.contentConfigs?.map(config => Number(config)),
                    meetingParticipationConfigs: result.campaign.meetingParticipationConfigs?.map(config =>
                        Number(config)
                    ),
                    needAgreeProtocol:
                        result.campaign.needAgreeProtocol == null
                            ? undefined
                            : Number(result.campaign.needAgreeProtocol)
                };
                setErrorMessage(null);
                setActivityState(normalizedCampaign.state);
                setPcImgMainFile(result.pcImgMainFile);
                setMobileImgMainFile(result.mobileImgMainFile);
                setPaymentQrCodeFile(result.paymentQrCodeFile);
                setScheduleFile(result.scheduleFile);
                setMeetingSupportFiles(result.meetingSupportFiles || []);
                setConferenceHotelFiles(result.conferenceHotelFiles || []);
                setPastGloriesFiles(result.pastGloriesFiles || []);
                if (result.offlineAccount && normalizedCampaign.offlineAccountId != null) {
                    setBankOptions([
                        {
                            label: [
                                result.offlineAccount.name,
                                result.offlineAccount.account,
                                result.offlineAccount.openingBank
                            ]
                                .filter(Boolean)
                                .join(" | "),
                            value: normalizedCampaign.offlineAccountId
                        }
                    ]);
                }
                if (normalizedCampaign.handlerId != null && normalizedCampaign.handlerName) {
                    // 编辑态先注入当前活动专员，避免禁用下拉框只显示原始 ID。
                    setSpecials([
                        {
                            id: normalizedCampaign.handlerId,
                            name: normalizedCampaign.handlerName
                        }
                    ]);
                    // 旧接口详情只返回专员姓名；补查完整专员资料以恢复“姓名 | 工号”展示。
                    void loadSpecials(normalizedCampaign.handlerName)
                        .then(items => {
                            const current = items
                                .map(normalizeActivitySpecial)
                                .find(item => String(item.id) === String(normalizedCampaign.handlerId));
                            if (current) setSpecials([current]);
                        })
                        .catch(() => undefined);
                }
                // Vue 接口使用手机号数组，表单输入框只回显当前活动专员的首个号码。
                form.setFieldsValue({
                    ...(normalizedCampaign as ActivityFormValues),
                    campaignMeetingTypeConfigForms: result.configs?.reduce<ActivityRegistrationConfig[]>(
                        (configs, config) => {
                            if (config.type) configs[config.type - 1] = config;
                            return configs;
                        },
                        []
                    ),
                    campaignDemandConfigForms: result.demandConfigs,
                    handlerMobile: normalizedCampaign.handlerMobile?.[0] ?? "",
                    scheduleFileId:
                        normalizedCampaign.scheduleFileId == null
                            ? undefined
                            : String(normalizedCampaign.scheduleFileId),
                    meetingSupportFileIds: formatFileIdInput(normalizedCampaign.meetingSupportFileIds),
                    conferenceHotelFileIds: formatFileIdInput(normalizedCampaign.conferenceHotelFileIds),
                    pastGlories: formatFileIdInput(normalizedCampaign.pastGlories)
                });
                if (campaignId != null && normalizedCampaign.contentConfigs?.includes(2)) {
                    void loadRelatedProducts(campaignId)
                        .then(items => {
                            setLoadedRelatedProductItems(items);
                            // 保留 Vue 编辑接口的 productId/productCampaign 结构，选择器只负责可读文本展示。
                            form.setFieldsValue({ relatedProductItems: items });
                        })
                        .catch(() => setErrorMessage("活动关联商品加载失败"));
                }
            })
            .catch(() => setErrorMessage("活动详情加载失败"));
    }, [campaignId, detailId, form, load, loadRelatedProducts]);

    /** 校验活动主图，保持旧版 UploadList 的格式与 10MB 限制。 */
    const validateMainImage = (label: "PC端活动主图" | "移动端活动主图", file: File) => {
        if (!ACTIVITY_MAIN_IMAGE_TYPES.has(file.type)) {
            setErrorMessage(`${label}仅支持 JPEG、PNG、WEBP、AVIF 格式`);
            return false;
        }
        if (file.size > ACTIVITY_MAIN_IMAGE_MAX_SIZE) {
            setErrorMessage(`${label}大小不能超过 10MB`);
            return false;
        }
        setErrorMessage(null);
        return true;
    };

    /** 校验并上传活动主图，成功后只回填 Vue 约定的文件 ID 字段。 */
    const uploadMainImage = async (
        field: "pcImgMainId" | "mobileImgMainId",
        label: "PC端活动主图" | "移动端活动主图",
        { file, onError, onSuccess }: UploadRequestOptions
    ) => {
        const formData = new FormData();
        formData.append("file", file);
        setErrorMessage(null);
        try {
            // 上传服务仅在用户选择文件后加载，避免影响不涉及上传的活动详情测试和首屏加载。
            const { fileService } = await import("@/api/services/fileService");
            const uploadedFile = await fileService.uploadFile(formData);
            if (uploadedFile.id == null) throw new Error("uploaded file id is missing");
            form.setFieldsValue({ [field]: uploadedFile.id });
            if (field === "pcImgMainId") setPcImgMainFile(uploadedFile as ActivityFile);
            else setMobileImgMainFile(uploadedFile as ActivityFile);
            onSuccess(uploadedFile);
        } catch (error) {
            setErrorMessage(`${label}上传失败`);
            onError(error instanceof Error ? error : new Error(`${label}上传失败`));
        }
    };

    /** 校验报名区域图片，收款码沿用 30MB，其余首页图片沿用 10MB。 */
    const validateMediaImage = (label: string, file: File, maxSizeInMb: number) => {
        if (!ACTIVITY_MAIN_IMAGE_TYPES.has(file.type)) {
            setErrorMessage(`${label}仅支持 JPEG、PNG、WEBP、AVIF 格式`);
            return false;
        }
        if (file.size > maxSizeInMb * 1024 * 1024) {
            setErrorMessage(`${label}大小不能超过 ${maxSizeInMb}MB`);
            return false;
        }
        setErrorMessage(null);
        return true;
    };

    /** 上传报名区域图片并同步 Vue 文件 ID 字段与缩略图。 */
    const uploadMediaImage = async (
        field:
            | "paymentQrCodeFileId"
            | "scheduleFileId"
            | "meetingSupportFileIds"
            | "conferenceHotelFileIds"
            | "pastGlories",
        label: string,
        files: ActivityFile[],
        updateFiles: (files: ActivityFile[]) => void,
        multiple: boolean,
        { file, onError, onSuccess }: UploadRequestOptions
    ) => {
        const formData = new FormData();
        formData.append("file", file);
        setErrorMessage(null);
        try {
            const { fileService } = await import("@/api/services/fileService");
            const uploadedFile = (await fileService.uploadFile(formData)) as ActivityFile;
            if (uploadedFile.id == null) throw new Error("uploaded file id is missing");
            const nextFiles = multiple ? [...files, uploadedFile] : [uploadedFile];
            updateFiles(nextFiles);
            form.setFieldsValue({
                [field]: multiple ? nextFiles.map(item => item.id).filter(id => id != null) : uploadedFile.id
            });
            onSuccess(uploadedFile);
        } catch (error) {
            setErrorMessage(`${label}上传失败`);
            onError(error instanceof Error ? error : new Error(`${label}上传失败`));
        }
    };

    const submit = async (draft = false) => {
        try {
            // Vue 草稿允许暂不填写正式提交字段，因此草稿保存只读取当前表单值。
            const values = draft ? form.getFieldsValue() : await form.validateFields();
            const payload = { ...values };
            if (!draft) {
                // 活动结束时间必须晚于开始时间，避免提交 Vue 接口无法接受的时间范围。
                const startTime = Date.parse(String(values.validTimeStart ?? ""));
                const finishTime = Date.parse(String(values.validTimeFinish ?? ""));
                if (Number.isFinite(startTime) && Number.isFinite(finishTime) && finishTime <= startTime) {
                    setErrorMessage("结束时间需大于开始时间");
                    return;
                }
                const currentTime = Date.now();
                // Vue 新增和已关闭活动禁止开始时间早于当前时刻，已开启活动保留历史时间兼容。
                if (Number.isFinite(startTime) && activityState !== 1 && startTime < currentTime) {
                    setErrorMessage("开始时间不能选择此刻之前的时间");
                    return;
                }
                // Vue 所有正式活动都要求结束时间晚于当前时刻。
                if (Number.isFinite(finishTime) && finishTime < currentTime) {
                    setErrorMessage("结束时间需大于当前时间");
                    return;
                }
            }
            setErrorMessage(null);
            // 沿用 Vue 表单的手机号规则，并将单个输入值转换成接口要求的数组。
            const handlerMobiles = Array.isArray(values.handlerMobile)
                ? values.handlerMobile.filter(Boolean).map(value => String(value).trim())
                : values.handlerMobile
                  ? [String(values.handlerMobile).trim()]
                  : [];
            if (!draft && !handlerMobiles.length) {
                setErrorMessage("请输入活动专员联系电话");
                return;
            }
            if (!draft && handlerMobiles.some(mobile => !/^1[3456789]\d{9}$/.test(mobile))) {
                setErrorMessage("请输入正确的手机号");
                return;
            }
            // Vue 正式活动必须同时提供 PC 和移动端主图文件 ID，草稿允许暂缺。
            if (!draft && !payload.pcImgMainId) {
                setErrorMessage("请上传PC端活动主图");
                return;
            }
            if (!draft && !payload.mobileImgMainId) {
                setErrorMessage("请上传移动端活动主图");
                return;
            }
            if (handlerMobiles.length) payload.handlerMobile = handlerMobiles;
            else delete payload.handlerMobile;
            // 空专员不提交字段，保持 Vue 新增表单的可选草稿契约。
            if (!payload.handlerId) delete payload.handlerId;
            else payload.handlerId = Number(payload.handlerId);
            if (payload.needAgreeProtocol == null || String(payload.needAgreeProtocol) === "") {
                delete payload.needAgreeProtocol;
            } else {
                payload.needAgreeProtocol = Number(payload.needAgreeProtocol);
            }
            // 协议字段只有选择同意协议时才提交，避免沿用旧草稿残留值。
            if (payload.needAgreeProtocol !== 1) {
                delete payload.protocolTitle;
                delete payload.protocolContent;
            }
            if (!payload.contactInfo) delete payload.contactInfo;
            if (payload.scheduleFileId == null || String(payload.scheduleFileId).trim() === "") {
                delete payload.scheduleFileId;
            } else {
                payload.scheduleFileId = Number(payload.scheduleFileId);
            }
            const fileIdFields = ["meetingSupportFileIds", "conferenceHotelFileIds", "pastGlories"] as const;
            fileIdFields.forEach(field => {
                const values = parseFileIdList(payload[field]);
                if (values.length) payload[field] = values;
                else delete payload[field];
            });
            const parsedRelatedProductItems = parseRelatedProductItems(payload.relatedProductItems);
            const relatedProductItems = parsedRelatedProductItems.map(item => {
                // Vue type=4 活动商品提交时需要保留 productCampaign，并补上当前活动 ID。
                if (item.type !== 4) return item;
                const source = loadedRelatedProductItems.find(
                    loadedItem =>
                        String(getRelatedProductReference(loadedItem)) === String(getRelatedProductReference(item))
                );
                if (!source?.productCampaign) return item;
                return {
                    type: 4,
                    product: {
                        ...source.productCampaign,
                        campaignId: Number.isFinite(Number(campaignId)) ? Number(campaignId) : campaignId
                    }
                };
            });
            // Vue 正式活动勾选商品配置时至少需要一条关联商品，草稿仍允许暂不添加。
            if (!draft && payload.contentConfigs?.includes(2) && !relatedProductItems.length) {
                setErrorMessage("勾选活动商品功能，请至少添加一个商品");
                return;
            }
            if (relatedProductItems.length) payload.relatedProductItems = relatedProductItems;
            else delete payload.relatedProductItems;
            if (Array.isArray(payload.campaignMeetingTypeConfigForms)) {
                const configuredParticipationForms = payload.campaignMeetingTypeConfigForms
                    .map((config, index) => ({ ...config, type: config?.type ?? index + 1 }))
                    .filter(config => {
                        const values = Object.entries(config).filter(([key]) => key !== "type");
                        return values.some(([, value]) => value !== undefined && value !== "" && value !== null);
                    });
                if (configuredParticipationForms.length) {
                    payload.campaignMeetingTypeConfigForms = configuredParticipationForms;
                } else {
                    delete payload.campaignMeetingTypeConfigForms;
                }
            }
            if (!payload.campaignDemandConfigForms?.length) delete payload.campaignDemandConfigForms;
            // Ant Form 会为已挂载但未填写的字段生成 undefined，提交前移除以保持 Vue payload 形状。
            Object.entries(payload).forEach(([key, value]) => {
                if (value === undefined) delete payload[key as keyof typeof payload];
            });
            if (draft) await saveDraft(payload);
            else await save(campaignId, payload);
            void navigate("/productActivity/activityList");
        } catch {
            setErrorMessage("活动保存失败");
        }
    };

    // 只有用户展开/聚焦专员选择时才查询，避免表单初次进入产生额外请求。
    const loadHandlers = async () => {
        try {
            setSpecials((await loadSpecials("")).map(normalizeActivitySpecial));
        } catch {
            setErrorMessage("活动专员加载失败");
        }
    };

    /** 远程查询启用中的平台收款账户，展示文本保持 Vue 的“名称 | 账号 | 开户行”。 */
    const loadBankAccounts = async (keyword = "") => {
        try {
            const { platBankAccountService } = await import("@/api/services/platBankAccountService");
            const accounts = await platBankAccountService.bankSelector({ state: 1, keyWord: keyword });
            setBankOptions(
                accounts.map(account => ({
                    label: `${account.name} | ${account.account} | ${account.openingBank}`,
                    value: account.id
                }))
            );
        } catch {
            setErrorMessage("收款账户加载失败");
        }
    };

    return (
        <section className="activity-form-page" aria-label="activity-form-page">
            <section className="section-card activity-form__header">
                <ZhDetailHeader title={campaignTitle} />
            </section>
            {errorMessage ? (
                <p className="activity-form__alert" role="alert">
                    {errorMessage}
                </p>
            ) : null}
            <Form
                className="mb-96"
                colon={false}
                form={form}
                labelAlign="right"
                labelCol={{ flex: "136px" }}
                wrapperCol={{ flex: 1 }}
            >
                <ActivityFormSection title="活动信息" className="activity-form__section--activity">
                    <ZhGrid cols={24} gap={[32, 0]}>
                        <ZhGridItem
                            xs={{ span: 24 }}
                            sm={{ span: 24 }}
                            md={{ span: 12 }}
                            lg={{ span: 16 }}
                            xl={{ span: 12 }}
                        >
                            <Form.Item
                                className="activity-form__title-item"
                                label="活动标题"
                                name="title"
                                rules={[{ required: true, message: "请输入活动标题" }]}
                            >
                                <Input allowClear maxLength={100} placeholder="请输入活动标题" showCount />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem
                            xs={{ span: 24 }}
                            sm={{ span: 24 }}
                            md={{ span: 12 }}
                            lg={{ span: 8 }}
                            xl={{ span: 6 }}
                        >
                            <Form.Item className="activity-form__date-range-item" label="活动时间范围" required>
                                <div ref={dateRangePickerRef}>
                                    <ZhDateRangePicker
                                        endPlaceholder="结束时间"
                                        format="YYYY-MM-DD HH:mm"
                                        prefixIcon={
                                            <svg
                                                aria-hidden="true"
                                                className="activity-form__date-icon"
                                                viewBox="0 0 1024 1024"
                                            >
                                                <path
                                                    d="M512 896a384 384 0 1 0 0-768 384 384 0 0 0 0 768m0 64a448 448 0 1 1 0-896 448 448 0 0 1 0 896"
                                                    fill="currentColor"
                                                />
                                                <path
                                                    d="M480 256a32 32 0 0 1 32 32v256a32 32 0 0 1-64 0V288a32 32 0 0 1 32-32"
                                                    fill="currentColor"
                                                />
                                                <path
                                                    d="M480 512h256q32 0 32 32t-32 32H480q-32 0-32-32t32-32"
                                                    fill="currentColor"
                                                />
                                            </svg>
                                        }
                                        rangeSeparator="-"
                                        startPlaceholder="开始时间"
                                        type="datetimerange"
                                        value={[
                                            validTimeStart ? String(validTimeStart) : null,
                                            validTimeFinish ? String(validTimeFinish) : null
                                        ]}
                                        // Vue form 使用分钟精度提交活动时间，不能把秒字段带入 campaign payload。
                                        valueFormat="YYYY-MM-DD HH:mm"
                                        onChange={(_value, formattedValue) => {
                                            // 以控件当前输入为准，兼容 zhihao 控件和测试替身的回调快照差异。
                                            const inputs =
                                                dateRangePickerRef.current?.querySelectorAll<HTMLInputElement>(
                                                    'input[aria-label="开始时间"], input[aria-label="结束时间"]'
                                                );
                                            const currentValues =
                                                inputs && inputs.length >= 2
                                                    ? [inputs[0]?.value, inputs[1]?.value]
                                                    : formattedValue;
                                            form.setFieldsValue({
                                                validTimeStart: currentValues?.[0] || undefined,
                                                validTimeFinish: currentValues?.[1] || undefined
                                            });
                                        }}
                                    />
                                    {/* 隐藏输入保留 Vue 字段与自动化测试可编辑能力。 */}
                                    <input
                                        aria-label="开始时间"
                                        className="sr-only"
                                        type="text"
                                        value={validTimeStart ? String(validTimeStart) : ""}
                                        onChange={event => form.setFieldsValue({ validTimeStart: event.target.value })}
                                    />
                                    <input
                                        aria-label="结束时间"
                                        className="sr-only"
                                        type="text"
                                        value={validTimeFinish ? String(validTimeFinish) : ""}
                                        onChange={event => form.setFieldsValue({ validTimeFinish: event.target.value })}
                                    />
                                </div>
                            </Form.Item>
                        </ZhGridItem>
                        <Form.Item noStyle name="validTimeStart">
                            <Input type="hidden" />
                        </Form.Item>
                        <Form.Item noStyle name="validTimeFinish">
                            <Input type="hidden" />
                        </Form.Item>
                        <ZhGridItem
                            xs={{ span: 24 }}
                            sm={{ span: 24 }}
                            md={{ span: 12 }}
                            lg={{ span: 8 }}
                            xl={{ span: 6 }}
                        >
                            <Form.Item className="activity-form__meeting-address-item" label="会议地址" name="meetingAddress">
                                <Input allowClear maxLength={100} placeholder="请输入会议地址" showCount />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem
                            xs={{ span: 24 }}
                            sm={{ span: 24 }}
                            md={{ span: 24 }}
                            lg={{ span: 24 }}
                            xl={{ span: 12 }}
                        >
                            <Form.Item label="PC端活动主图" required>
                                <ActivityImageUpload
                                    accept=".jpg,.jpeg,.png,.webp,.avif,image/jpeg,image/png,image/webp,image/avif"
                                    beforeUpload={file => validateMainImage("PC端活动主图", file)}
                                    hint="支持JPEG、PNG、WEBP、AVIF格式，单张最大10MB，建议尺寸1280 X 334px，请勿包含敏感信息或侵权内容"
                                    httpRequest={options =>
                                        void uploadMainImage("pcImgMainId", "PC端活动主图", options)
                                    }
                                    image={pcImgMainFile}
                                    label="PC端活动主图"
                                    uploadedId={pcImgMainId}
                                />
                            </Form.Item>
                        </ZhGridItem>
                        <Form.Item name="pcImgMainId" hidden>
                            <Input type="hidden" />
                        </Form.Item>
                        <ZhGridItem
                            xs={{ span: 24 }}
                            sm={{ span: 24 }}
                            md={{ span: 24 }}
                            lg={{ span: 24 }}
                            xl={{ span: 12 }}
                        >
                            <Form.Item label="移动端活动主图" required>
                                <ActivityImageUpload
                                    accept=".jpg,.jpeg,.png,.webp,.avif,image/jpeg,image/png,image/webp,image/avif"
                                    beforeUpload={file => validateMainImage("移动端活动主图", file)}
                                    hint="支持JPEG、PNG、WEBP、AVIF格式，单张最大10MB，建议尺寸1280 X 334px，请勿包含敏感信息或侵权内容"
                                    httpRequest={options =>
                                        void uploadMainImage("mobileImgMainId", "移动端活动主图", options)
                                    }
                                    image={mobileImgMainFile}
                                    label="移动端活动主图"
                                    uploadedId={mobileImgMainId}
                                />
                            </Form.Item>
                        </ZhGridItem>
                        <Form.Item name="mobileImgMainId" hidden>
                            <Input type="hidden" />
                        </Form.Item>
                        <ZhGridItem
                            xs={{ span: 24 }}
                            sm={{ span: 24 }}
                            md={{ span: 12 }}
                            lg={{ span: 8 }}
                            xl={{ span: 6 }}
                        >
                            <Form.Item label="活动内容配置" name="contentConfigs">
                                <Checkbox.Group
                                    disabled={formLocked}
                                    options={[
                                        { label: "活动报名", value: 1 },
                                        { label: "活动商品", value: 2 }
                                    ]}
                                />
                            </Form.Item>
                        </ZhGridItem>
                    </ZhGrid>
                </ActivityFormSection>

                {selectedContentConfigs.includes(1) ? (
                    <>
                        <ActivityFormSection title="报名通道配置" className="activity-form__section--activity">
                            <span className="sr-only">报名信息配置</span>
                            <ZhGrid cols={24} gap={[32, 0]}>
                                <ZhGridItem
                                    xs={{ span: 24 }}
                                    sm={{ span: 24 }}
                                    md={{ span: 18 }}
                                    lg={{ span: 16 }}
                                    xl={{ span: 12 }}
                                >
                                    <Form.Item label="线下支付收款账户" name="offlineAccountId" required>
                                        <Select
                                            allowClear
                                            filterOption={false}
                                            options={bankOptions}
                                            placeholder="请选择收款账户"
                                            showSearch
                                            onFocus={() => void loadBankAccounts()}
                                            onSearch={keyword => void loadBankAccounts(keyword)}
                                            onChange={() => {
                                                form.setFieldsValue({ paymentQrCodeFileId: undefined });
                                                setPaymentQrCodeFile(undefined);
                                            }}
                                        />
                                    </Form.Item>
                                </ZhGridItem>
                                <ZhGridItem span={24}>
                                    <Form.Item label="收款码">
                                        <ActivityMediaUpload
                                            accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                                            beforeUpload={file => validateMediaImage("收款码", file, 30)}
                                            files={paymentQrCodeFile ? [paymentQrCodeFile] : []}
                                            hint="支持上传JPG、JPEG、PNG、WEBP格式的图片，单张最大30MB"
                                            httpRequest={options =>
                                                void uploadMediaImage(
                                                    "paymentQrCodeFileId",
                                                    "收款码",
                                                    paymentQrCodeFile ? [paymentQrCodeFile] : [],
                                                    files => setPaymentQrCodeFile(files[0]),
                                                    false,
                                                    options
                                                )
                                            }
                                            label="收款码"
                                            limit={1}
                                        />
                                    </Form.Item>
                                    <Form.Item name="paymentQrCodeFileId" noStyle>
                                        <Input
                                            aria-label="收款码文件 ID"
                                            className="activity-form__legacy-control"
                                            placeholder="请输入收款码文件 ID"
                                        />
                                    </Form.Item>
                                </ZhGridItem>
                                <ZhGridItem span={24}>
                                    <Form.Item label="参会形式配置" name="meetingParticipationConfigs" required>
                                        <Checkbox.Group disabled={formLocked} options={PARTICIPATION_OPTIONS} />
                                    </Form.Item>
                                </ZhGridItem>
                            </ZhGrid>

                            {selectedParticipationConfigs.map(type => {
                                const option = PARTICIPATION_OPTIONS.find(item => item.value === Number(type));
                                const configIndex = Number(type) - 1;
                                const registrationConfig = meetingConfigs[configIndex];
                                if (!option || configIndex < 0) return null;
                                return (
                                    <section className="activity-form__base-card" key={option.value}>
                                        <div className="activity-form__base-card-title">{option.label}</div>
                                        <div className="activity-form__base-card-content">
                                            <ZhGrid cols={24} gap={[32, 0]}>
                                                <ZhGridItem
                                                    xs={{ span: 24 }}
                                                    sm={{ span: 24 }}
                                                    md={{ span: 12 }}
                                                    lg={{ span: 8 }}
                                                    xl={{ span: 6 }}
                                                >
                                                    <Form.Item
                                                        label="报名费"
                                                        name={[
                                                            "campaignMeetingTypeConfigForms",
                                                            configIndex,
                                                            "registrationFee"
                                                        ]}
                                                        required
                                                    >
                                                        <InputNumber
                                                            controls={false}
                                                            min={0}
                                                            placeholder="请输入报名费"
                                                            suffix={Number(type) === 5 ? "元/人" : "元"}
                                                        />
                                                    </Form.Item>
                                                </ZhGridItem>
                                                {Number(type) !== 5 ? (
                                                    <ZhGridItem
                                                        xs={{ span: 24 }}
                                                        sm={{ span: 24 }}
                                                        md={{ span: 12 }}
                                                        lg={{ span: 8 }}
                                                        xl={{ span: 6 }}
                                                    >
                                                        <Form.Item
                                                            label="参会最大人数"
                                                            name={[
                                                                "campaignMeetingTypeConfigForms",
                                                                configIndex,
                                                                "maxParticipants"
                                                            ]}
                                                            required
                                                        >
                                                            <InputNumber
                                                                controls={false}
                                                                min={1}
                                                                placeholder="请输入人数"
                                                                suffix="人"
                                                            />
                                                        </Form.Item>
                                                    </ZhGridItem>
                                                ) : null}
                                                <ZhGridItem span={24}>
                                                    <Form.Item label="字段配置" required>
                                                        <div className="activity-form__fields">
                                                            {PARTICIPATION_FIELD_NAMES.map(fieldName => (
                                                                (() => {
                                                                    const configuredField = registrationConfig?.fieldConfig?.find(
                                                                        field => field.fieldName === fieldName
                                                                    );
                                                                    const selected = registrationConfig?.fieldConfig
                                                                        ? Boolean(configuredField)
                                                                        : ["姓名", "手机号"].includes(fieldName);
                                                                    const required = configuredField
                                                                        ? Number(configuredField.hasRequired) === 1
                                                                        : ["姓名", "手机号"].includes(fieldName);
                                                                    return (
                                                                        <span
                                                                            className="activity-form__field-option"
                                                                            key={fieldName}
                                                                        >
                                                                            <Checkbox disabled={formLocked} defaultChecked={selected}>
                                                                                {fieldName}
                                                                            </Checkbox>
                                                                            <Select
                                                                                disabled={formLocked}
                                                                                defaultValue={required ? 1 : 0}
                                                                                options={[
                                                                                    { label: "必填", value: 1 },
                                                                                    { label: "选填", value: 0 }
                                                                                ]}
                                                                            />
                                                                        </span>
                                                                    );
                                                                })()
                                                            ))}
                                                        </div>
                                                    </Form.Item>
                                                </ZhGridItem>
                                                <ZhGridItem span={24}>
                                                    <Form.Item
                                                        label="权益介绍"
                                                        name={[
                                                            "campaignMeetingTypeConfigForms",
                                                            configIndex,
                                                            "rightsIntroduction"
                                                        ]}
                                                    >
                                                        <ActivityRichTextEditor height="120px" legacyLabel="权益介绍" />
                                                    </Form.Item>
                                                </ZhGridItem>
                                                <ZhGridItem
                                                    xs={{ span: 24 }}
                                                    sm={{ span: 24 }}
                                                    md={{ span: 12 }}
                                                    lg={{ span: 6 }}
                                                >
                                                    <Form.Item
                                                        label="是否赠送会员"
                                                        name={[
                                                            "campaignMeetingTypeConfigForms",
                                                            configIndex,
                                                            "isGiveMember"
                                                        ]}
                                                        required
                                                    >
                                                        <Radio.Group
                                                            options={[
                                                                { label: "是", value: 1 },
                                                                { label: "否", value: 0 }
                                                            ]}
                                                        />
                                                    </Form.Item>
                                                </ZhGridItem>
                                                <ZhGridItem span={24}>
                                                    <Form.Item
                                                        label="合同模版"
                                                        name={[
                                                            "campaignMeetingTypeConfigForms",
                                                            configIndex,
                                                            "contractTemplateFileId"
                                                        ]}
                                                    >
                                                        <ActivityContractTemplateUpload />
                                                    </Form.Item>
                                                </ZhGridItem>
                                            </ZhGrid>
                                        </div>
                                    </section>
                                );
                            })}

                            <ZhGrid cols={4} gap={[16, 0]}>
                                <ZhGridItem>
                                    <Form.Item label="报名需同意协议" name="needAgreeProtocol" required>
                                        <Radio.Group
                                            options={[
                                                { label: "是", value: 1 },
                                                { label: "否", value: 0 }
                                            ]}
                                        />
                                    </Form.Item>
                                </ZhGridItem>
                                {Number(needAgreeProtocol) === 1 ? (
                                    <>
                                        <ZhGridItem>
                                            <Form.Item label="协议标题" name="protocolTitle" required>
                                                <Input maxLength={200} placeholder="请输入协议标题" showCount />
                                            </Form.Item>
                                        </ZhGridItem>
                                        <ZhGridItem span={4}>
                                            <Form.Item label="协议内容" name="protocolContent" required>
                                                <ActivityRichTextEditor legacyLabel="协议内容" />
                                            </Form.Item>
                                        </ZhGridItem>
                                    </>
                                ) : null}
                            </ZhGrid>
                        </ActivityFormSection>

                        <ActivityFormSection title="需求对接配置">
                            {demandConfigs.length ? (
                                <ZhGrid cols={4} gap={[16, 0]}>
                                    {demandConfigs.map((config, index) => (
                                        <ZhGridItem key={`${config.fieldName ?? config.label ?? "demand"}-${index}`}>
                                            <Form.Item
                                                label={`需求字段${index + 1}`}
                                                name={["campaignDemandConfigForms", index, "fieldName"]}
                                            >
                                                <Input placeholder="请输入需求字段" />
                                            </Form.Item>
                                        </ZhGridItem>
                                    ))}
                                </ZhGrid>
                            ) : (
                                <div className="activity-form__demand-table" aria-label="需求对接配置列表">
                                    <div className="activity-form__demand-toolbar">新增</div>
                                    <div className="activity-form__demand-grid">
                                        <div>序号</div>
                                        <div>字段名称</div>
                                        <div>是否必填</div>
                                        <div>输入类型</div>
                                        <div>操作</div>
                                        <div className="activity-form__demand-no-data">暂无数据</div>
                                    </div>
                                </div>
                            )}
                        </ActivityFormSection>

                        <ActivityFormSection title="日程安排配置">
                            <Form.Item label="日程安排">
                                <ActivityMediaUpload
                                    accept=".jpg,.jpeg,.png,.webp,.avif,image/jpeg,image/png,image/webp,image/avif"
                                    beforeUpload={file => validateMediaImage("日程安排", file, 10)}
                                    files={scheduleFile ? [scheduleFile] : []}
                                    hint="支持JPEG、PNG、WEBP、AVIF格式，单张最大10MB，请勿包含敏感信息或侵权内容"
                                    httpRequest={options =>
                                        void uploadMediaImage(
                                            "scheduleFileId",
                                            "日程安排",
                                            scheduleFile ? [scheduleFile] : [],
                                            files => setScheduleFile(files[0]),
                                            false,
                                            options
                                        )
                                    }
                                    label="日程安排"
                                    limit={1}
                                />
                            </Form.Item>
                            <Form.Item name="scheduleFileId" noStyle>
                                <Input aria-label="日程安排文件 ID" className="activity-form__legacy-control" />
                            </Form.Item>
                        </ActivityFormSection>

                        <ActivityFormSection title="首页内容配置">
                            <Form.Item label="联系方式" name="contactInfo">
                                <ActivityRichTextEditor height="120px" legacyLabel="活动联系方式" />
                            </Form.Item>
                            {[
                                {
                                    field: "meetingSupportFileIds" as const,
                                    label: "会议支持",
                                    files: meetingSupportFiles,
                                    update: setMeetingSupportFiles,
                                    legacyLabel: "会议支持文件 ID"
                                },
                                {
                                    field: "conferenceHotelFileIds" as const,
                                    label: "会议酒店",
                                    files: conferenceHotelFiles,
                                    update: setConferenceHotelFiles,
                                    legacyLabel: "会议酒店文件 ID"
                                },
                                {
                                    field: "pastGlories" as const,
                                    label: "历届风采",
                                    files: pastGloriesFiles,
                                    update: setPastGloriesFiles,
                                    legacyLabel: "历届风采文件 ID"
                                }
                            ].map(media => (
                                <div key={media.field}>
                                    <Form.Item label={media.label}>
                                        <ActivityMediaUpload
                                            accept=".jpg,.jpeg,.png,.webp,.avif,image/jpeg,image/png,image/webp,image/avif"
                                            beforeUpload={file => validateMediaImage(media.label, file, 10)}
                                            files={media.files}
                                            hint="支持JPEG、PNG、WEBP、AVIF格式，单张最大10MB，建议图片长宽比例3:2，请勿包含敏感信息或侵权内容"
                                            httpRequest={options =>
                                                void uploadMediaImage(
                                                    media.field,
                                                    media.label,
                                                    media.files,
                                                    media.update,
                                                    true,
                                                    options
                                                )
                                            }
                                            label={media.label}
                                            limit={4}
                                        />
                                    </Form.Item>
                                    <Form.Item name={media.field} noStyle>
                                        <Input
                                            aria-label={media.legacyLabel}
                                            className="activity-form__legacy-control"
                                        />
                                    </Form.Item>
                                </div>
                            ))}
                        </ActivityFormSection>
                    </>
                ) : null}

                {selectedContentConfigs.includes(2) ? (
                    <ActivityFormSection title="商品信息配置">
                        <Form.Item label="关联商品配置" name="relatedProductItems">
                            <ActivityRelatedProductSelector loadCandidates={loadRelatedProductCandidates} />
                        </Form.Item>
                    </ActivityFormSection>
                ) : null}

                <ActivityFormSection title="其他信息" className="activity-form__section--other">
                    <ZhGrid cols={24} gap={[32, 0]}>
                        <ZhGridItem
                            xs={{ span: 24 }}
                            sm={{ span: 24 }}
                            md={{ span: 8 }}
                            lg={{ span: 8 }}
                            xl={{ span: 8 }}
                        >
                            <Form.Item label="活动专员" name="handlerId" required>
                                <Select
                                    allowClear
                                    disabled={formLocked}
                                    filterOption={false}
                                    options={specials.map(special => ({ label: special.name, value: special.id }))}
                                    placeholder="请选择活动专员"
                                    showSearch
                                    onFocus={() => void loadHandlers()}
                                    onSearch={keyword =>
                                        void loadSpecials(keyword).then(items =>
                                            setSpecials(items.map(normalizeActivitySpecial))
                                        )
                                    }
                                />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem
                            xs={{ span: 24 }}
                            sm={{ span: 24 }}
                            md={{ span: 24 }}
                            lg={{ span: 18 }}
                            xl={{ span: 18 }}
                        >
                            <Form.Item label="活动专员联系电话" name="handlerMobile">
                                <Input placeholder="请输入活动专员联系电话" />
                            </Form.Item>
                        </ZhGridItem>
                    </ZhGrid>
                </ActivityFormSection>

                <Space className="operation-fixed-btn" size={0}>
                    {!formLocked ? (
                        <Button aria-label="保存草稿" onClick={() => void submit(true)}>
                            保存
                        </Button>
                    ) : null}
                    <Button aria-label="提交活动" type="primary" onClick={() => void submit()}>
                        提交
                    </Button>
                    <Button onClick={() => void navigate("/productActivity/activityList")}>返回</Button>
                </Space>
            </Form>
        </section>
    );
}

export default ActivityFormPage;
